import React, { useState } from "react";
import {
  Input,
  Stack,
  Button,
  Center,
  Heading,
  useToast,
  FormControl,
  FormErrorMessage,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { signIn } from "../../services/authService";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../contexts/AuthContext";
import PasswordInput from "../password-input/PasswordInput";

function Login() {
  const { t } = useTranslation("common");
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [showPassword, setShowPassword] = useState(false);

  const mutation = useMutation({
    mutationFn: (loginCredentials) => {
      return signIn(loginCredentials);
    },
    onError: (error) => {
      const message =
        error.response?.status === 401
          ? t("loginErrorMessage")
          : error.response?.data?.errors?.[0] ||
            error.response?.data?.errors?.full_messages?.[0] ||
            t("loginErrorMessage");

      toast({
        title: message,
        status: "error",
      });
    },
    onSuccess: (data) => {
      const {
        id,
        email,
        firstName,
        lastName,
        type,
        emailConfirmed,
        telephoneNumber,
        accountKind,
        organizationName,
        contactPersonName,
        displayName,
        contactPerson,
      } = data.data.data;
      login({
        id,
        email,
        firstName,
        lastName,
        type,
        emailConfirmed,
        telephoneNumber,
        accountKind,
        organizationName,
        contactPersonName,
        displayName,
        contactPerson,
      });
      navigate(decodeURIComponent(searchParams.get("returnUrl") || "/"));
    },
  });

  return (
    <Center>
      <Stack spacing={3} width={"400px"}>
        <Heading size={"lg"} mb={8}>
          {t("loginHeading")}
        </Heading>
        <Formik
          initialValues={{
            email: "",
            password: "",
          }}
          validationSchema={Yup.object({
            email: Yup.string()
              .email(t("invalidEmailMessage"))
              .required(t("requiredFieldMessage")),
            password: Yup.string().required(t("requiredFieldMessage")),
          })}
          onSubmit={(values) => {
            mutation.mutate(values);
          }}
        >
          {(props) => (
            <Form>
              <Stack spacing={3}>
                <Field name="email">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.email && form.touched.email}
                    >
                      <Input
                        {...field}
                        placeholder={t("emailInputPlaceholder")}
                        variant="filled"
                      />
                      <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="password">
                  {({ field, form }) => {
                    return (
                      <FormControl
                        isInvalid={
                          form.errors.password && form.touched.password
                        }
                      >
                        <PasswordInput
                          {...field}
                          variant="filled"
                          isVisible={showPassword}
                          onToggle={() => setShowPassword(!showPassword)}
                          placeholder={t("passwordInputPlaceholder")}
                        />
                        <FormErrorMessage>
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    );
                  }}
                </Field>
                <ChakraLink
                  as={RouterLink}
                  to="/forgot-password"
                  fontSize="sm"
                  color="teal.600"
                  alignSelf="flex-end"
                >
                  {t("forgotPasswordLinkText")}
                </ChakraLink>
                <Button
                  width="full"
                  isLoading={mutation.isLoading}
                  type="submit"
                  colorScheme="whatsapp"
                  mt={4}
                >
                  {t("login")}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>
    </Center>
  );
}

export default Login;
